import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ShieldCheck, Scale, Building2, TrendingUp, ArrowRight, Award, FileCheck, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";
import SEO from "@/components/SEO";

const HERO_BG = "https://images.unsplash.com/photo-1723110994499-df46435aa4b3?w=1920&q=85";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [search, setSearch] = useState({ q: "", property_type: "", price: "" });
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/properties/featured?limit=6").then((r) => setFeatured(r.data.items || []));
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (search.q) params.set("q", search.q);
    if (search.property_type) params.set("property_type", search.property_type);
    if (search.price) {
      const [min, max] = search.price.split("-");
      if (min) params.set("min_price", min);
      if (max) params.set("max_price", max);
    }
    navigate(`/properties?${params.toString()}`);
  };

  return (
    <div data-testid="home-page">
      <SEO
        title="Better Properties. Better Decisions."
        description="Discover residential, commercial, plot and agricultural properties in Rewa with transparency, legal verification, and confidence. Tharlo — premium property discovery."
        image={HERO_BG}
      />
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/40 to-ink/20" />
        </div>
        <div className="relative container-tharlo w-full pb-20 pt-32">
          <div className="max-w-3xl">
            <h1 className="font-display text-white text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] animate-fade-up" style={{ animationDelay: "100ms" }}>
              Better Properties.<br /><em className="text-gold not-italic">Better Decisions.</em>
            </h1>
            <p className="mt-6 text-white/80 text-lg max-w-xl leading-relaxed animate-fade-up" style={{ animationDelay: "200ms" }}>
              Discover residential, commercial, plot and agricultural properties with transparency,
              legal verification, and confidence — curated for serious buyers and investors.
            </p>
          </div>

          {/* Search */}
          <form onSubmit={onSearch} data-testid="hero-search" className="mt-10 glass shadow-2xl p-3 max-w-4xl flex flex-col md:flex-row gap-2 animate-fade-up" style={{ animationDelay: "300ms" }}>
            <div className="flex-1 flex items-center gap-2 px-4 py-2 border-r border-border/40">
              <MapPin className="w-4 h-4 text-gold" />
              <input data-testid="hero-search-q" value={search.q} onChange={(e)=>setSearch({...search, q:e.target.value})} placeholder="Search by locality, project or area" className="w-full bg-transparent outline-none text-sm" />
            </div>
            <select data-testid="hero-search-type" value={search.property_type} onChange={(e)=>setSearch({...search, property_type:e.target.value})} className="px-4 py-3 bg-transparent text-sm outline-none border-r border-border/40">
              <option value="">All Types</option>
              <option value="residential">Residential</option>
              <option value="commercial">Commercial</option>
              <option value="plot">Plot</option>
              <option value="agricultural">Agricultural</option>
            </select>
            <select data-testid="hero-search-price" value={search.price} onChange={(e)=>setSearch({...search, price:e.target.value})} className="px-4 py-3 bg-transparent text-sm outline-none">
              <option value="">Any Budget</option>
              <option value="0-3000000">Under ₹30 Lac</option>
              <option value="3000000-7000000">₹30L – ₹70L</option>
              <option value="7000000-15000000">₹70L – ₹1.5 Cr</option>
              <option value="15000000-">Above ₹1.5 Cr</option>
            </select>
            <button data-testid="hero-search-btn" type="submit" className="btn-primary !bg-ink">
              <Search className="w-4 h-4" /> Explore
            </button>
          </form>

          <div className="mt-8 flex flex-wrap gap-3 animate-fade-up" style={{ animationDelay: "400ms" }}>
            <Link to="/properties" data-testid="hero-cta-explore" className="btn-gold">Explore Properties</Link>
            <Link to="/sell" data-testid="hero-cta-sell" className="bg-white text-ink hover:bg-gold hover:text-white border border-white px-6 py-3 text-sm font-semibold tracking-wide transition-colors">Sell Your Property</Link>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="container-tharlo py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="overline mb-3">Categories</div>
            <h2 className="font-display text-3xl lg:text-4xl text-ink tracking-tight">Discover by Property Type</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { key: "residential", label: "Residential", img: "https://images.unsplash.com/photo-1724582586529-62622e50c0b3?w=600&q=80", note: "Villas, apartments, builder floors" },
            { key: "commercial", label: "Commercial", img: "https://images.pexels.com/photos/27307400/pexels-photo-27307400.jpeg?w=600", note: "Showrooms, offices, warehouses" },
            { key: "plot", label: "Plots", img: "https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=600&q=80", note: "Residential & commercial land" },
            { key: "agricultural", label: "Agricultural", img: "https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=600&q=80", note: "Farmland & farmhouses" },
          ].map((c) => (
            <Link key={c.key} to={`/properties?property_type=${c.key}`} data-testid={`category-${c.key}`} className="group relative aspect-[4/5] overflow-hidden block">
              <img src={c.img} alt={c.label} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                <h3 className="font-display text-2xl">{c.label}</h3>
                <p className="text-xs text-white/70 mt-1">{c.note}</p>
                <div className="mt-3 inline-flex items-center gap-1 text-gold text-xs font-semibold uppercase tracking-wider">
                  Explore <ArrowRight className="w-3 h-3" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="bg-white py-20">
        <div className="container-tharlo">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="overline mb-3">Curated · Verified</div>
              <h2 className="font-display text-3xl lg:text-4xl text-ink tracking-tight">Featured Premium Properties</h2>
            </div>
            <Link to="/properties" data-testid="featured-view-all" className="hidden md:inline-flex items-center gap-2 text-sm font-semibold text-ink hover:text-gold">View All <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((p, i) => <PropertyCard key={p.id} p={p} index={i} />)}
          </div>
        </div>
      </section>

      {/* WHY THARLO - bento */}
      <section className="container-tharlo py-20">
        <div className="overline mb-3">Why Tharlo</div>
        <h2 className="font-display text-3xl lg:text-5xl text-ink tracking-tight max-w-3xl">A premium platform built on <em className="text-gold not-italic">trust, transparency, and curation</em>.</h2>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
          <div className="md:col-span-7 bg-ink text-white p-8 lg:p-12 flex flex-col justify-between min-h-[280px]">
            <ShieldCheck className="w-10 h-10 text-gold" />
            <div>
              <h3 className="font-display text-3xl mb-2">Verified Listings</h3>
              <p className="text-white/70 text-sm max-w-md">Every property goes through Tharlo's verification process — ownership, legal status, road access, and infrastructure checks.</p>
            </div>
          </div>
          <div className="md:col-span-5 bg-gold text-white p-8 lg:p-12 flex flex-col justify-between min-h-[280px]">
            <Scale className="w-10 h-10 text-white" />
            <div>
              <h3 className="font-display text-3xl mb-2">Legal Transparency</h3>
              <p className="text-white/85 text-sm">In-house legal verification, title checks, and registry support from trusted property lawyers.</p>
            </div>
          </div>
          <div className="md:col-span-4 bg-white border border-border p-8 flex flex-col justify-between min-h-[240px]">
            <Building2 className="w-9 h-9 text-gold" />
            <div>
              <h3 className="font-display text-2xl text-ink mb-2">Trusted Broker Network</h3>
              <p className="text-foreground/70 text-sm">Brokers, developers, and owners — all working with Tharlo's quality standards. We don't compete with brokers, we empower them.</p>
            </div>
          </div>
          <div className="md:col-span-4 bg-white border border-border p-8 flex flex-col justify-between min-h-[240px]">
            <Award className="w-9 h-9 text-gold" />
            <div>
              <h3 className="font-display text-2xl text-ink mb-2">Premium Locations</h3>
              <p className="text-foreground/70 text-sm">Hand-picked properties in well-connected, high-appreciation neighbourhoods only.</p>
            </div>
          </div>
          <div className="md:col-span-4 bg-white border border-border p-8 flex flex-col justify-between min-h-[240px]">
            <TrendingUp className="w-9 h-9 text-gold" />
            <div>
              <h3 className="font-display text-2xl text-ink mb-2">Future Price Discovery</h3>
              <p className="text-foreground/70 text-sm">Tharlo Fair Price ranges and upcoming bidding tools to bring data-driven pricing to Tier-2 cities.</p>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-white py-20">
        <div className="container-tharlo">
          <div className="overline mb-3">How it works</div>
          <h2 className="font-display text-3xl lg:text-4xl text-ink tracking-tight">A simple, transparent process</h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-10">
            <div>
              <h3 className="font-display text-2xl text-ink mb-6">For Buyers</h3>
              <ol className="space-y-5">
                {["Explore curated, verified listings", "Verify legal status & connectivity", "Connect with Tharlo advisors", "Purchase with confidence"].map((s, i) => (
                  <li key={s} className="flex gap-4">
                    <div className="w-8 h-8 bg-ink text-gold font-display text-lg flex items-center justify-center shrink-0">{i+1}</div>
                    <p className="text-foreground/80 pt-1">{s}</p>
                  </li>
                ))}
              </ol>
            </div>
            <div>
              <h3 className="font-display text-2xl text-ink mb-6">For Sellers & Brokers</h3>
              <ol className="space-y-5">
                {["Submit property details & images", "Review by Tharlo verification team", "Publish to qualified buyer network", "Receive premium, qualified leads"].map((s, i) => (
                  <li key={s} className="flex gap-4">
                    <div className="w-8 h-8 bg-gold text-white font-display text-lg flex items-center justify-center shrink-0">{i+1}</div>
                    <p className="text-foreground/80 pt-1">{s}</p>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="container-tharlo py-20">
        <div className="overline mb-3">Testimonials</div>
        <h2 className="font-display text-3xl lg:text-4xl text-ink tracking-tight">Trusted by buyers, brokers, and owners</h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { name: "Rajesh Sharma", role: "Property Investor, NRI", quote: "Tharlo's legal verification saved me from a disputed plot. Their fair price range is a game-changer for Rewa." },
            { name: "Anita Mishra", role: "Home Buyer", quote: "Premium curation. Felt advisory, not transactional. We bought our home in 3 weeks." },
            { name: "Vinod Kumar", role: "Broker, Rewa", quote: "Tharlo brings me serious buyers. The platform respects brokers — finally someone gets it." },
          ].map((t) => (
            <div key={t.name} className="bg-white border border-border p-8">
              <FileCheck className="w-8 h-8 text-gold mb-4" />
              <p className="text-foreground/80 italic leading-relaxed">"{t.quote}"</p>
              <div className="mt-6 pt-4 border-t border-border">
                <div className="font-semibold text-ink">{t.name}</div>
                <div className="text-xs text-muted-foreground">{t.role}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative bg-ink text-white py-24">
        <div className="container-tharlo text-center max-w-3xl mx-auto">
          <div className="overline text-gold mb-4">Ready to make a move?</div>
          <h2 className="font-display text-4xl lg:text-5xl tracking-tight">Ready to Buy or Sell <em className="text-gold not-italic">Property</em>?</h2>
          <p className="mt-5 text-white/70">Talk to Tharlo's advisors for a verified, transparent property journey.</p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/properties" data-testid="cta-explore" className="btn-gold">Explore Listings</Link>
            <Link to="/sell" data-testid="cta-submit" className="bg-white text-ink hover:bg-gold hover:text-white px-6 py-3 text-sm font-semibold tracking-wide transition-colors">Submit Property</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
